import { t } from "../../i18n";
import { useEffect, useState } from "react";
import { Coins, Cpu, Hash, LoaderCircle } from "lucide-react";
import { api } from "../../api";
import { SettingsPage } from "../settings/ProfilePage";
import { UsageSummaryMetric } from "../../components/shared/UsageSummaryMetric";
import {
  normalizeUsage,
  usageExportJson,
  type UsageData,
} from "./UsagePage.utils";
type OrganizationEntitlements = {
  executionEnabled: boolean;
  maxSeats: number | null;
  monthlyPromptLimit: number | null;
  monthlySpendMicros: number | null;
  maxConcurrentGenerations: number | null;
  maxConcurrentProviderDiscoveries: number | null;
  maxToolCallsPerGeneration: number | null;
  maxSubagentFanout: number | null;
  maxWorkspaceRuntimeSeconds: number | null;
  maxConcurrentSshConnections: number | null;
  maxSshConnectionAttemptsPerMinute: number | null;
  storageLimitBytes: number | null;
};

const number = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});
const money = (micros: number) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(micros / 1_000_000);

export function UsagePage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<UsageData>();
  const [entitlements, setEntitlements] = useState<OrganizationEntitlements>();
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [alerts, setAlerts] = useState<
    Array<{ id: string; thresholdBps: number; channel: string }>
  >([]);
  const [alertThreshold, setAlertThreshold] = useState(8000);
  useEffect(() => {
    setData(undefined);
    setError("");
    api<unknown>(`/organization/usage?days=${days}`)
      .then((next) => setData(normalizeUsage(next)))
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Usage could not be loaded"),
      );
    void api<OrganizationEntitlements>("/organization/entitlements")
      .then(setEntitlements)
      .catch(() => setEntitlements(undefined));
    void api<typeof alerts>("/organization/budget-alerts")
      .then(setAlerts)
      .catch(() => {});
  }, [days]);
  const addAlert = async () => {
    const alert = await api<(typeof alerts)[number]>(
      "/organization/budget-alerts",
      {
        method: "POST",
        body: JSON.stringify({
          thresholdBps: alertThreshold,
          channel: "in_app",
        }),
      },
    );
    setAlerts((current) => [...current, alert]);
  };
  const exportUsage = async () => {
    setExporting(true);
    setError("");
    try {
      const result = await api<unknown>(
        `/organization/usage?days=${days}&download=1`,
      );
      const blob = new Blob([usageExportJson(result)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `lumen-organization-usage-${days}d.json`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Usage could not be exported",
      );
    } finally {
      setExporting(false);
    }
  };
  const maxDaily = Math.max(
    1,
    ...(data?.daily.map((row) => row.totalTokens) ?? []),
  );
  return (
    <SettingsPage
      title={t("Usage")}
      description="Organization-wide model activity, token consumption, and estimated provider cost."
      action={
        <div className="flex gap-2">
          <button
            className="button"
            type="button"
            disabled={exporting}
            onClick={() => void exportUsage()}
          >
            {exporting ? t("Exporting…") : t("Export usage")}
          </button>
          <select
            className="field w-auto"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            <option value={7}>{t("Last 7 days")}</option>
            <option value={30}>{t("Last 30 days")}</option>
            <option value={90}>{t("Last 90 days")}</option>
          </select>
        </div>
      }
    >
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      {!data && !error && (
        <div className="grid min-h-64 place-items-center text-stone-400">
          <LoaderCircle className="animate-spin" />
        </div>
      )}
      {data && (
        <div className="space-y-6">
          {entitlements && (
            <section className="panel p-5">
              <h3 className="font-bold">{t("Execution limits")}</h3>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "These limits are enforced for this organization and are managed by site permissions. SSH and Proxmox capacity is separate from host approval and capability access.",
                )}
              </p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  [
                    "Concurrent generations",
                    entitlements.maxConcurrentGenerations,
                    "active",
                  ],
                  [
                    "Provider discovery requests",
                    entitlements.maxConcurrentProviderDiscoveries,
                    "active",
                  ],
                  [
                    "Tool calls per generation",
                    entitlements.maxToolCallsPerGeneration,
                    "calls",
                  ],
                  ["Subagent fanout", entitlements.maxSubagentFanout, "agents"],
                  [
                    "Workspace runtime",
                    entitlements.maxWorkspaceRuntimeSeconds,
                    "seconds",
                  ],
                  [
                    "SSH / Proxmox connections",
                    entitlements.maxConcurrentSshConnections,
                    "connections",
                  ],
                  [
                    "SSH connection attempts",
                    entitlements.maxSshConnectionAttemptsPerMinute,
                    "per minute",
                  ],
                ].map(([label, value, unit]) => (
                  <div
                    key={String(label)}
                    className="rounded-xl border border-stone-200 p-3 dark:border-white/10"
                  >
                    <span className="block text-xs font-bold tracking-wider text-stone-500 uppercase">
                      {t(String(label))}
                    </span>
                    <strong className="mt-1 block text-lg">
                      {value == null ? t("Unlimited") : `${value} ${unit}`}
                    </strong>
                  </div>
                ))}
              </div>
            </section>
          )}
          <section className="panel p-5">
            <h3 className="font-bold">{t("Budget alerts")}</h3>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <select
                className="field w-auto"
                value={alertThreshold}
                onChange={(event) =>
                  setAlertThreshold(Number(event.target.value))
                }
              >
                {[50, 75, 80, 90, 100].map((threshold) => (
                  <option key={threshold} value={threshold * 100}>
                    {threshold}%
                  </option>
                ))}
              </select>
              <button
                className="button-primary"
                onClick={() => void addAlert()}
              >
                {t("Add alert")}
              </button>
              {alerts.map((alert) => (
                <span
                  key={alert.id}
                  className="rounded-full bg-stone-100 px-3 py-1 text-xs dark:bg-white/10"
                >
                  {alert.thresholdBps / 100}% · {alert.channel}
                </span>
              ))}
            </div>
          </section>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <UsageSummaryMetric
              label={t("Requests")}
              value={number.format(data.totals.requests)}
              icon={Hash}
            />
            <UsageSummaryMetric
              label={t("Total tokens")}
              value={`${number.format(data.totals.rawTokens)} / ${number.format(data.totals.ratedTokens)}`}
              icon={Cpu}
            />
            <UsageSummaryMetric
              label={t("Prompt / output")}
              value={`${number.format(data.totals.promptTokens)} / ${number.format(data.totals.completionTokens)}`}
              icon={Cpu}
            />
            <UsageSummaryMetric
              label={t("Cached input")}
              value={number.format(data.totals.cacheReadTokens)}
              icon={Cpu}
            />
            <UsageSummaryMetric
              label={t("Estimated cost")}
              value={`${money(data.totals.rawCostMicros)} / ${money(data.totals.ratedCostMicros)}`}
              icon={Coins}
            />
          </div>
          <section className="panel p-5">
            <h3 className="font-bold">{t("Daily tokens")}</h3>
            <div className="mt-5 flex h-40 items-end gap-1">
              {data.daily.length ? (
                data.daily.map((row) => (
                  <div
                    key={row.date}
                    className="group relative flex min-w-0 flex-1 items-end"
                    title={`${row.date}: ${row.totalTokens.toLocaleString()} tokens, ${money(row.costMicros)}`}
                  >
                    <div
                      className="w-full rounded-t bg-lumen-500/75 transition group-hover:bg-lumen-600"
                      style={{
                        height: `${Math.max(3, (row.totalTokens / maxDaily) * 100)}%`,
                      }}
                    />
                  </div>
                ))
              ) : (
                <div className="m-auto text-sm text-stone-400">
                  {t("No recorded usage in this period.")}
                </div>
              )}
            </div>
          </section>
          <div className="grid gap-6 xl:grid-cols-2">
            <section className="panel overflow-hidden">
              <div className="border-b border-stone-100 p-5 font-bold dark:border-white/5">
                {t("By model")}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs tracking-wider text-stone-400 uppercase">
                    <tr>
                      <th className="p-4">{t("Model")}</th>
                      <th className="p-4 text-right">{t("Requests")}</th>
                      <th className="p-4 text-right">{t("Tokens")}</th>
                      <th className="p-4 text-right">{t("Cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byModel.map((row) => (
                      <tr
                        key={`${row.modelId}-${row.modelRemoteId}-${row.kind}`}
                        className="border-t border-stone-100 dark:border-white/5"
                      >
                        <td className="p-4">
                          <div className="font-semibold">
                            {row.modelName?.trim() || row.modelRemoteId}
                          </div>
                          {row.kind === "command-approval" && (
                            <div className="text-xs font-medium text-lumen-700 dark:text-lumen-300">
                              {t("Command approval model")}
                            </div>
                          )}
                          {row.kind === "compaction" && (
                            <div className="text-xs font-medium text-lumen-700 dark:text-lumen-300">
                              {t("Compaction summary model")}
                            </div>
                          )}
                          <div className="text-xs text-stone-400">
                            {row.modelRemoteId}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          {row.requests.toLocaleString()}
                        </td>
                        <td className="p-4 text-right">
                          {row.totalTokens.toLocaleString()}
                        </td>
                        <td className="p-4 text-right">
                          {money(row.costMicros)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="panel overflow-hidden">
              <div className="border-b border-stone-100 p-5 font-bold dark:border-white/5">
                {t("By person")}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs tracking-wider text-stone-400 uppercase">
                    <tr>
                      <th className="p-4">{t("Person")}</th>
                      <th className="p-4 text-right">{t("Requests")}</th>
                      <th className="p-4 text-right">{t("Tokens")}</th>
                      <th className="p-4 text-right">{t("Cost")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byUser.map((row) => (
                      <tr
                        key={row.userId}
                        className="border-t border-stone-100 dark:border-white/5"
                      >
                        <td className="p-4">
                          <div className="font-semibold">{row.name}</div>
                        </td>
                        <td className="p-4 text-right">
                          {row.requests.toLocaleString()}
                        </td>
                        <td className="p-4 text-right">
                          {row.totalTokens.toLocaleString()}
                        </td>
                        <td className="p-4 text-right">
                          {money(row.costMicros)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>
      )}
    </SettingsPage>
  );
}
